import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const nl = {
  nav: {
    tagline: "Vaccinatiebeheer voor families",
    links: {
      app: "Wat is Vaxy?",
      travel: "Reizen",
      community: "Community",
      about: "Over ons",
      media: "Media",
      blog: "Artikelen",
    },
    companion: "Together Protected",
    getApp: "Download app",
    scanQr: "Scan om Vaxy te downloaden",
    qrAlt: "QR-code om de Vaxy app te downloaden",
  },
  footer: {
    blurb: "Het digitale vaccinatieboekje voor jou en je gezin.",
    product: "Product",
    theApp: "De app",
    travelHealth: "Reisgezondheid",
    apiDocs: "API-documentatie",
    download: "Download",
    community: "Community",
    about: "Over ons",
    media: "Media",
    blog: "Artikelen",
    support: "Support",
    privacy: "Privacy",
    cookies: "Cookies",
    terms: "Voorwaarden",
    faq: "Veelgestelde vragen",
    contact: "Contact",
    deleteAccount: "Account verwijderen",
    madeWith: "Gemaakt met zorg voor gezondere reizen",
    rights: "Alle rechten voorbehouden.",
  },
  store: {
    appStoreAlt: "Download in de App Store",
    playAlt: "Download in Google Play",
  },
  notFound: {
    title: "Pagina niet gevonden",
    body: "De pagina die je zoekt bestaat niet of is verplaatst.",
    home: "Terug naar home",
  },
  disclaimer:
    "De informatie in Vaxy is uitsluitend bedoeld ter ondersteuning van persoonlijke gezondheidsadministratie en vormt geen medisch advies. Vaxy is geen zorgaanbieder.",
};

const en: ChromeCopy = {
  nav: {
    tagline: "Vaccination management for families",
    links: {
      app: "What is Vaxy?",
      travel: "Travel",
      community: "Community",
      about: "About us",
      media: "Media",
      blog: "Articles",
    },
    companion: "Together Protected",
    getApp: "Get App",
    scanQr: "Scan to get Vaxy App",
    qrAlt: "QR code to download the Vaxy app",
  },
  footer: {
    blurb: "Your digital vaccination record and travel health companion.",
    product: "Product",
    theApp: "The app",
    travelHealth: "Travel health",
    apiDocs: "API documentation",
    download: "Download",
    community: "Community",
    about: "About us",
    media: "Media",
    blog: "Articles",
    support: "Support",
    privacy: "Privacy",
    cookies: "Cookies",
    terms: "Terms",
    faq: "FAQ",
    contact: "Contact",
    deleteAccount: "Delete account",
    madeWith: "Made with care for healthier journeys",
    rights: "All rights reserved.",
  },
  store: {
    appStoreAlt: "Download on the App Store",
    playAlt: "Get it on Google Play",
  },
  notFound: {
    title: "Page not found",
    body: "The page you are looking for does not exist or has moved.",
    home: "Back to home",
  },
  disclaimer:
    "The information in Vaxy is intended solely to support personal health administration and does not constitute medical advice. Vaxy is not a healthcare provider.",
};

const de: ChromeCopy = {
  nav: {
    tagline: "Impfmanagement für Familien",
    links: {
      app: "Was ist Vaxy?",
      travel: "Reisen",
      community: "Community",
      about: "Über uns",
      media: "Medien",
      blog: "Artikel",
    },
    companion: "Together Protected",
    getApp: "App laden",
    scanQr: "Scannen, um Vaxy zu laden",
    qrAlt: "QR-Code zum Herunterladen der Vaxy App",
  },
  footer: {
    blurb: "Dein digitaler Impfpass und Begleiter für Reisegesundheit.",
    product: "Produkt",
    theApp: "Die App",
    travelHealth: "Reisegesundheit",
    apiDocs: "API-Dokumentation",
    download: "Download",
    community: "Community",
    about: "Über uns",
    media: "Medien",
    blog: "Artikel",
    support: "Support",
    privacy: "Datenschutz",
    cookies: "Cookies",
    terms: "Bedingungen",
    faq: "FAQ",
    contact: "Kontakt",
    deleteAccount: "Konto löschen",
    madeWith: "Mit Sorgfalt für gesündere Reisen gemacht",
    rights: "Alle Rechte vorbehalten.",
  },
  store: {
    appStoreAlt: "Im App Store laden",
    playAlt: "Bei Google Play herunterladen",
  },
  notFound: {
    title: "Seite nicht gefunden",
    body: "Die gesuchte Seite existiert nicht oder wurde verschoben.",
    home: "Zur Startseite",
  },
  disclaimer:
    "Die Informationen in Vaxy dienen ausschließlich der Unterstützung der persönlichen Gesundheitsverwaltung und stellen keine medizinische Beratung dar. Vaxy ist kein Gesundheitsdienstleister.",
};

const fr: ChromeCopy = {
  nav: {
    tagline: "Gestion vaccinale pour les familles",
    links: {
      app: "Qu'est-ce que Vaxy ?",
      travel: "Voyage",
      community: "Communauté",
      about: "À propos",
      media: "Médias",
      blog: "Articles",
    },
    companion: "Together Protected",
    getApp: "Télécharger",
    scanQr: "Scannez pour obtenir Vaxy",
    qrAlt: "QR code pour télécharger l'application Vaxy",
  },
  footer: {
    blurb:
      "Votre carnet de vaccination numérique et compagnon santé en voyage.",
    product: "Produit",
    theApp: "L'application",
    travelHealth: "Santé voyage",
    apiDocs: "Documentation API",
    download: "Télécharger",
    community: "Communauté",
    about: "À propos",
    media: "Médias",
    blog: "Articles",
    support: "Aide",
    privacy: "Confidentialité",
    cookies: "Cookies",
    terms: "Conditions",
    faq: "FAQ",
    contact: "Contact",
    deleteAccount: "Supprimer mon compte",
    madeWith: "Conçu avec soin pour des voyages plus sains",
    rights: "Tous droits réservés.",
  },
  store: {
    appStoreAlt: "Télécharger dans l'App Store",
    playAlt: "Disponible sur Google Play",
  },
  notFound: {
    title: "Page introuvable",
    body: "La page que vous cherchez n'existe pas ou a été déplacée.",
    home: "Retour à l'accueil",
  },
  disclaimer:
    "Les informations dans Vaxy servent uniquement à soutenir la gestion personnelle de la santé et ne constituent pas un avis médical. Vaxy n'est pas un prestataire de soins.",
};

const es: ChromeCopy = {
  nav: {
    tagline: "Gestión de vacunas para familias",
    links: {
      app: "¿Qué es Vaxy?",
      travel: "Viajes",
      community: "Comunidad",
      about: "Sobre nosotros",
      media: "Medios",
      blog: "Artículos",
    },
    companion: "Together Protected",
    getApp: "Descargar",
    scanQr: "Escanea para descargar Vaxy",
    qrAlt: "Código QR para descargar la app Vaxy",
  },
  footer: {
    blurb:
      "Tu cartilla de vacunación digital y compañera de salud para viajar.",
    product: "Producto",
    theApp: "La app",
    travelHealth: "Salud en viajes",
    apiDocs: "Documentación API",
    download: "Descargar",
    community: "Comunidad",
    about: "Sobre nosotros",
    media: "Medios",
    blog: "Artículos",
    support: "Ayuda",
    privacy: "Privacidad",
    cookies: "Cookies",
    terms: "Condiciones",
    faq: "Preguntas frecuentes",
    contact: "Contacto",
    deleteAccount: "Eliminar cuenta",
    madeWith: "Creado con cuidado para viajes más saludables",
    rights: "Todos los derechos reservados.",
  },
  store: {
    appStoreAlt: "Descargar en App Store",
    playAlt: "Disponible en Google Play",
  },
  notFound: {
    title: "Página no encontrada",
    body: "La página que buscas no existe o se ha movido.",
    home: "Volver al inicio",
  },
  disclaimer:
    "La información de Vaxy está destinada únicamente a apoyar la administración personal de la salud y no constituye asesoramiento médico. Vaxy no es un proveedor sanitario.",
};

export type ChromeCopy = typeof nl;
export const chrome: Record<Locale, ChromeCopy> = copy(nl, en, de, fr, es);
